import React, { useState, useEffect, ReactElement } from 'react';
import {
  Modal,
  Button,
  FormItem,
  TextArea,
  Fieldset,
  Alert,
  FileUpload,
} from 'elmo-elements';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export interface AIGenerateModalProps {
  isOpened: boolean;
  onBack: () => void;
  onClose: () => void;
  onGenerate: (data: { prompt: string; file: File }) => void;
  isLoading: boolean;
}

const AIGenerateModal = ({
  isOpened,
  onBack,
  onClose,
  onGenerate,
  isLoading,
}: AIGenerateModalProps): ReactElement => {
  const [prompt, setPrompt] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (!isOpened) {
      setPrompt('');
      setUploadedFile(null);
      setFileError('');
    }
  }, [isOpened]);

  const handleDropAccepted = (acceptedFiles: any[]) => {
    const candidate = acceptedFiles[0] as File;
    setFileError('');
    if (candidate.type !== 'application/pdf') {
      setFileError('Please select a PDF file');
      return;
    }
    if (candidate.size > MAX_FILE_SIZE_BYTES) {
      setFileError('File size must be less than 50MB');
      return;
    }
    setUploadedFile(candidate);
  };

  const handleDropRejected = () => {
    setFileError('Please select a PDF file under 50MB');
  };

  const isReady = prompt.trim().length > 0 && uploadedFile !== null;

  const handleGenerate = () => {
    if (isReady && !isLoading) {
      onGenerate({ prompt: prompt.trim(), file: uploadedFile! });
    }
  };

  return (
    <Modal
      id="AIGenerateModal"
      className="new-module"
      isOpened={isOpened}
      title="New Module"
      closeModal={onClose}
      primaryButton={
        <div className="new-module__footer-actions">
          <Button type="default" isUppercase={false} onClick={onBack}>
            Back
          </Button>
          <Button
            type="primary"
            isUppercase={false}
            isDisabled={!isReady || isLoading}
            isLoading={isLoading}
            onClick={handleGenerate}
          >
            Generate with AI
          </Button>
        </div>
      }
    >
      <Fieldset>
        <FormItem label="AI module prompt *">
          <TextArea
            name="aiPrompt"
            value={prompt}
            onChange={(e: any) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Describe what you want the AI to generate..."
          />
        </FormItem>

        <FormItem label="Reference PDF *">
          {!uploadedFile && (
            <FileUpload
              key={String(isOpened)}
              name="pdf-upload"
              displayDropArea
              displayFiles={false}
              isMulti={false}
              disabled={false}
              fileMaxSize={50}
              acceptedFiles={['.pdf', 'application/pdf']}
              dropareaText="Choose PDF file or drag and drop (max 50MB)"
              onDropAccepted={handleDropAccepted}
              onDropRejected={handleDropRejected}
            />
          )}

          {uploadedFile && (
            <div className="file-row">
              <span className="file-row__name">✓ {uploadedFile.name}</span>
              <span className="file-row__size">
                {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB • PDF
              </span>
              <Button
                type="default"
                isUppercase={false}
                className="file-row__remove"
                onClick={() => {
                  setUploadedFile(null);
                  setFileError('');
                }}
              >
                Remove
              </Button>
            </div>
          )}

          {fileError && <Alert type="danger" message={fileError} />}
        </FormItem>
      </Fieldset>
    </Modal>
  );
};

export default AIGenerateModal;
